/**
 * AiAgentProvider — NgRx facade that dispatches actions and exposes selectors.
 *
 * Components use this provider instead of directly interacting with the store.
 */
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { AiAgentJobRequest } from '../interfaces/ai-agent-job-request.interface';
import { AiAgentSession } from '../interfaces/ai-agent-session.interface';
import { AiAgentStatus } from '../interfaces/ai-agent-status.enum';
import { AiJobProgressEvent } from '../interfaces/ai-agent-progress-event.interface';
import { AiBackendError } from '../interfaces/ai-backend-error';
import * as AiAgentActions from '../actions/ai-agent.actions';
import {
    selectActiveSessionId,
    selectSession,
    selectStatus,
    selectProgressEvents,
    selectOutput,
    selectIsSubmitting,
    selectError,
    selectSocketConnected,
} from '../reducer/ai-agent.reducer';
import { IAiAgentProvider } from './ai-agent.provider.interface';

@Injectable({ providedIn: 'root' })
export class AiAgentProvider implements IAiAgentProvider {
    // ── Selectors ─────────────────────────────────────────────────────

    readonly activeSessionId$: Observable<string | null> = this.store.select(selectActiveSessionId);
    readonly session$: Observable<AiAgentSession | null> = this.store.select(selectSession);
    readonly status$: Observable<AiAgentStatus | null> = this.store.select(selectStatus);
    readonly progressEvents$: Observable<AiJobProgressEvent[]> = this.store.select(selectProgressEvents);
    readonly output$: Observable<Record<string, unknown> | null> = this.store.select(selectOutput);
    readonly isSubmitting$: Observable<boolean> = this.store.select(selectIsSubmitting);
    readonly error$: Observable<AiBackendError | null> = this.store.select(selectError);
    readonly socketConnected$: Observable<boolean> = this.store.select(selectSocketConnected);

    constructor(private readonly store: Store) {}

    // ── Dispatch Helpers ──────────────────────────────────────────────

    submitJob(request: AiAgentJobRequest): void {
        this.store.dispatch(AiAgentActions.submitJob({ request }));
    }

    pollSession(sessionId: string): void {
        this.store.dispatch(AiAgentActions.pollSession({ sessionId }));
    }

    sendChat(sessionId: string, message: string, sectionKey?: string): void {
        this.store.dispatch(AiAgentActions.sendChat({ sessionId, message, ...(sectionKey ? { sectionKey } : {}) }));
    }

    connectSocket(token: string): void {
        this.store.dispatch(AiAgentActions.connectSocket({ token }));
    }

    disconnectSocket(): void {
        this.store.dispatch(AiAgentActions.disconnectSocket());
    }

    resetSession(): void {
        this.store.dispatch(AiAgentActions.resetSession());
    }
}
